import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import InfoArea from './InfoArea'
import SearchArea from './SearchArea'
import IconButton from './IconButton'
import IconLink from './IconLink'
import AccessibilityOverlay from './overlays/AccessibilityOverlay'
import PoiInformationOverlay from './overlays/PoiInformationOverlay'
import NavigationOverlay from './overlays/NavigationOverlay'
import { OverlayLayers } from '../constants'
import ActionButtonContainer from '../containers/ActionButtonContainer'
import useGeolocation, { isLocation } from '../queries/currentLocation'

const CURRENT_LOCATION_ZOOM = 15

const ContactButton = ({ feedbackForm }) => {
  const { latitude, longitude } = useGeolocation()

  return (
    <IconLink
      title='Neem contact op'
      target='_blank'
      rel='noreferrer'
      href={feedbackForm.replace('{latitude}', latitude).replace('{longitude}', longitude)}
    >
      <Image alt='Icoon van vlag' src='/assets/icons/flag.svg' width={16} height={16} />
    </IconLink>
  )
}

const AccessibilityButton = ({ active, handleClick }) => (
  <IconButton title='Toon toegankelijkheidsfilters' onClick={handleClick} active={active}>
    <Image
      alt='Icoon van een persoon'
      src='/assets/icons/accessibility.svg'
      width={16}
      height={16}
    />
  </IconButton>
)

const ReturnToCenterPointButton = ({ active, handleClick }) => (
  <IconButton title='Ga terug naar begin locatie' onClick={handleClick} active={active}>
    <Image
      alt='Icoon van een dromenvanger'
      src='/assets/icons/MyLocation.svg'
      width={16}
      height={16}
    />
  </IconButton>
)

const PanToCurrentLocationButton = ({ baseMap }) => {
  const { latitude, longitude } = useGeolocation()

  const handleClick = () => {
    if (isLocation({ latitude, longitude })) {
      baseMap.panTo({ lat: latitude, lng: longitude })
      baseMap.setZoom(CURRENT_LOCATION_ZOOM)
    }
  }

  return (
    <IconButton title='Focus op huidige locatie' onClick={handleClick}>
      <Image
        alt='Icoon van navigatiepijl'
        src='/assets/icons/navigation.svg'
        width={16}
        height={16}
      />
    </IconButton>
  )
}

const MapUI = ({
  event,
  baseMap,
  poiLayers,
  poiContent,
  setPoiContent,
  generateRoute,
  generateTravelAdvice,
  launchGoogleMaps,
  removeRoute,
}) => {
  const [showNavigationButtons, setShowNavigationButtons] = useState(false)
  const [filters, setFilters] = useState([])
  const [activeOverlay, setActiveOverlay] = useState(OverlayLayers.NONE)
  const currentLocation = useGeolocation()
  const hasLocationEnabled = isLocation(currentLocation)

  // add listerer to focus on map if map is clicked
  baseMap?.addListener('click', () => {
    handleOverlayClose()
  })

  useEffect(() => {
    poiLayers.map((poiLayer) => {
      poiLayer.dataLayer.forEach((poi) => {
        if (filters.length) {
          filters.map((filter) => {
            poiLayer.dataLayer.overrideStyle(poi, { visible: poi.getProperty(filter) })
          })
        } else {
          poiLayer.dataLayer.overrideStyle(poi, { visible: true })
        }
      })
    })
  }, [filters])

  useEffect(() => {
    handleOverlayChange(poiContent ? OverlayLayers.INFO : OverlayLayers.NONE)
  }, [poiContent])

  const handleOverlayChange = (value) => {
    setActiveOverlay(value)
  }

  const handleFilterClick = ({ currentTarget }) => {
    if (filters.includes(currentTarget.id)) {
      const newFilters = filters.filter((value) => {
        return value !== currentTarget.id
      })
      setFilters(newFilters)
    } else {
      setFilters([...filters, currentTarget.id])
    }
  }

  const handleOverlayClose = (shouldReset = true) => {
    if (shouldReset && poiContent) {
      setPoiContent(null)
    }
    handleOverlayChange()
  }

  const handleNavigationClick = () => {
    handleOverlayClose(false)
    if (poiContent?.getGeometry().getType() === 'Polygon') {
      setShowNavigationButtons(generateTravelAdvice(poiContent))
    } else setShowNavigationButtons(generateRoute())
  }

  const handleAccessibilityClick = () => {
    handleOverlayChange('filters')
  }

  const handleNavigationClose = () => {
    removeRoute()
    setShowNavigationButtons(false)
  }

  const handleReturnToCenterPointClick = () => {
    baseMap.panTo(event.centerPoint.center)
    baseMap.setZoom(event.centerPoint.zoom)
  }

  const canNavigateTo = poiContent?.getGeometry().getType() === 'Point'
  const canGiveTravelAdvise = poiContent?.getGeometry().getType() === 'Polygon'

  return (
    <div id='mapUI'>
      <ActionButtonContainer className={`${activeOverlay ? 'd-none' : 'd-flex'}`}>
        {hasLocationEnabled && <PanToCurrentLocationButton baseMap={baseMap} />}
        <ReturnToCenterPointButton handleClick={handleReturnToCenterPointClick} />
        <AccessibilityButton active={filters.length} handleClick={handleAccessibilityClick} />
        <ContactButton feedbackForm={event.feedbackForm.location} />
      </ActionButtonContainer>
      <InfoArea
        isOpen={activeOverlay === OverlayLayers.INFO || activeOverlay === OverlayLayers.FILTERS}
        handleClose={handleOverlayClose}
      >
        {activeOverlay === OverlayLayers.FILTERS && (
          <AccessibilityOverlay filters={filters} handleClick={handleFilterClick} />
        )}
        {activeOverlay === OverlayLayers.INFO && (
          <PoiInformationOverlay
            feedbackForm={event.feedbackForm.poi}
            feature={poiContent}
            canNavigateTo={canNavigateTo}
            // Specific for atmospheric areas
            canGiveTravelAdvise={canGiveTravelAdvise}
            handleNavigationClick={handleNavigationClick}
          />
        )}
      </InfoArea>
      <SearchArea
        isOpen={activeOverlay === OverlayLayers.SEARCH}
        event={event}
        baseMap={baseMap}
        poiLayers={poiLayers}
        setPoiContent={setPoiContent}
        handleOverlayChange={handleOverlayChange}
      />
      )
      {showNavigationButtons && (
        <NavigationOverlay
          handleClose={handleNavigationClose}
          launchGoogleMaps={launchGoogleMaps}
        />
      )}
    </div>
  )
}

export default React.memo(MapUI)
